import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApplicationAnswer,
  ApplicationExtraction,
  ApplyKit,
  ClassifiedField,
  GenerateAnswerRequest,
  UserProfile,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface ApplyError {
  status: number;
  error: string;
  detail?: string;
}

function toApplyError(err: unknown): ApplyError {
  if (err instanceof ApiError) {
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error:
        (typeof payload.error === "string" ? payload.error : err.message) ??
        "request_failed",
      detail: typeof payload.detail === "string" ? payload.detail : undefined,
    };
  }
  return { status: 0, error: "network_error" };
}

export interface ExtractResponse {
  extraction: ApplicationExtraction;
  classified: ClassifiedField[];
  cached: boolean;
}

export function useExtractApplication() {
  return useMutationToast<ExtractResponse, ApplyError, { application_url: string }>({
    mutationFn: async (input) => {
      try {
        return await api<ExtractResponse>("/api/apply/extract", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toApplyError(err);
      }
    },
    success: (data) =>
      data.cached
        ? `Loaded ${data.classified.length} cached field${data.classified.length === 1 ? "" : "s"}.`
        : `Extracted ${data.classified.length} field${data.classified.length === 1 ? "" : "s"}.`,
    errorPrefix: "Extract failed",
  });
}

export interface ApplyWorkshopState {
  application: {
    id: string;
    url: string;
    role: string;
    company_id: string | null;
    status: string;
  };
  extraction: ApplicationExtraction | null;
  classified: ClassifiedField[];
  answers: ApplicationAnswer[];
  profile: UserProfile | null;
}

export function useApplyWorkshop(applicationId: string | undefined) {
  return useQuery({
    queryKey: ["apply", applicationId ?? ""],
    enabled: !!applicationId,
    queryFn: () => api<ApplyWorkshopState>(`/api/apply/${applicationId}`),
    staleTime: 5_000,
  });
}

// The read-only copy-paste kit (GET /api/apply/:id/kit): the same data as the
// workshop, assembled server-side into form-ordered sections + a paste sheet.
export function useApplyKit(applicationId: string | undefined) {
  return useQuery({
    queryKey: ["apply-kit", applicationId ?? ""],
    enabled: !!applicationId,
    queryFn: () => api<ApplyKit>(`/api/apply/${applicationId}/kit`),
    staleTime: 5_000,
  });
}

export interface GenerateAnswerResponse {
  answer: ApplicationAnswer;
  voice_reference_ids: string[];
  career_unit_ids: string[];
  model: string;
}

export function useGenerateAnswer(applicationId: string | undefined) {
  const qc = useQueryClient();
  return useMutationToast<GenerateAnswerResponse, ApplyError, GenerateAnswerRequest>({
    mutationFn: async (input) => {
      try {
        return await api<GenerateAnswerResponse>(
          `/api/apply/${applicationId}/answer`,
          { method: "POST", body: JSON.stringify(input) },
        );
      } catch (err) {
        throw toApplyError(err);
      }
    },
    onSuccess: () => {
      if (applicationId) {
        qc.invalidateQueries({ queryKey: ["apply", applicationId] });
      }
    },
    success: "Drafted.",
    errorPrefix: "Draft failed",
  });
}

export function usePatchAnswer(applicationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, answer }: { id: string; answer: string }) =>
      api<{ answer: ApplicationAnswer }>(`/api/apply/answer/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ answer }),
      }),
    onSuccess: () => {
      if (applicationId) {
        qc.invalidateQueries({ queryKey: ["apply", applicationId] });
        // An edit changes the packet hash, so the kit + review queue must refetch.
        qc.invalidateQueries({ queryKey: ["apply-kit", applicationId] });
        qc.invalidateQueries({ queryKey: ["review-queue"] });
      }
    },
  });
}

export interface SubmitResponse {
  answers_marked: number;
  artifacts: Array<{ id: string; vector_id: string | null }>;
}

export function useSubmitApplication(applicationId: string | undefined) {
  const qc = useQueryClient();
  return useMutationToast<SubmitResponse, ApplyError, void>({
    mutationFn: async () => {
      try {
        return await api<SubmitResponse>(
          `/api/apply/${applicationId}/submit`,
          { method: "POST" },
        );
      } catch (err) {
        throw toApplyError(err);
      }
    },
    onSuccess: () => {
      if (applicationId) {
        qc.invalidateQueries({ queryKey: ["apply", applicationId] });
        qc.invalidateQueries({ queryKey: ["apply-kit", applicationId] });
        qc.invalidateQueries({ queryKey: ["applications"] });
      }
    },
    success: (data) =>
      `Marked ${data.answers_marked} answer${data.answers_marked === 1 ? "" : "s"} as submitted; ${data.artifacts.length} artifact${data.artifacts.length === 1 ? "" : "s"} saved.`,
    errorPrefix: "Submit failed",
  });
}
